// Global styles for the Emergency Locket App
export const fonts = {
  primary: "'Inter', 'Geist', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800
  }
};

export const colors = {
  // Emergency Red Palette
  primary: '#dc2626',      // Red-600
  primaryDark: '#b91c1c',  // Red-700
  primaryLight: '#ef4444', // Red-500
  secondary: '#14b8a6',    // Professional Teal
  secondaryDark: '#0d9488',
  secondaryLight: '#2dd4bf',
  danger: '#dc2626',       // Refined Crimson
  dangerDark: '#b91c1c',
  warning: '#f97316',      // Alert Orange
  warningDark: '#ea580c',
  success: '#10b981',
  successDark: '#059669',
  info: '#0891b2',
  infoDark: '#0e7490',
  medical: {
    blue: '#3b82f6',
    blueDark: '#2563eb',
    blueLight: '#60a5fa',
    red: '#dc2626',
    green: '#10b981',
    purple: '#8b5cf6'
  },
  gray: {
    50: '#f8fafc',         // Subtle off-white
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a'
  },
  white: '#ffffff',
  black: '#000000'
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px'
};

export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '12px',
  full: '50px'
};

export const shadows = {
  soft: '0 2px 8px rgba(15, 23, 42, 0.08)',    // Softer, subtle shadow
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  glass: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' // Deprecated: use md instead
};

export const commonStyles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: `0 ${spacing.md}`,
    '@media (max-width: 768px)': {
      padding: `0 ${spacing.sm}`
    }
  },
  
  mobileContainer: {
    maxWidth: '100%',
    margin: '0 auto',
    padding: spacing.md,
    minHeight: '100vh',
    backgroundColor: colors.gray[50]
  },

  card: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.xl,
    boxShadow: shadows.soft,
    border: `1px solid ${colors.gray[200]}`,
    padding: spacing.lg,
    marginBottom: spacing.md
  },

  button: {
    base: {
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.md,
      border: 'none',
      fontWeight: '500',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'color 0.15s ease, background-color 0.15s ease',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none',
      '&:disabled': {
        opacity: 0.6,
        cursor: 'not-allowed'
      }
    },
    primary: {
      backgroundColor: colors.primary,
      color: colors.white,
      '&:hover': {
        backgroundColor: colors.primaryDark
      }
    },
    secondary: {
      backgroundColor: colors.secondary,
      color: colors.white,
      '&:hover': {
        backgroundColor: colors.secondaryDark
      }
    },
    danger: {
      backgroundColor: colors.danger,
      color: colors.white,
      '&:hover': {
        backgroundColor: colors.dangerDark
      }
    },
    outline: {
      backgroundColor: 'transparent',
      color: colors.primary,
      border: `1px solid ${colors.primary}`,
      '&:hover': {
        backgroundColor: colors.primary,
        color: colors.white
      }
    },
    large: {
      padding: `${spacing.md} ${spacing.lg}`,
      fontSize: '16px',
      minHeight: '48px'
    },
    small: {
      padding: `${spacing.xs} ${spacing.sm}`,
      fontSize: '12px'
    }
  },

  input: {
    base: {
      width: '100%',
      padding: spacing.md,
      border: `1px solid ${colors.gray[300]}`,
      borderRadius: borderRadius.lg,
      fontSize: '16px',
      minHeight: '44px',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '&:focus': {
        outline: 'none',
        borderColor: colors.primary,
        boxShadow: `0 0 0 3px ${colors.primary}15`
      }
    },
    error: {
      borderColor: colors.danger,
      '&:focus': {
        borderColor: colors.danger,
        boxShadow: `0 0 0 3px ${colors.danger}20`
      }
    }
  },

  formGroup: {
    marginBottom: spacing.md,
  },

  label: {
    display: 'block',
    marginBottom: spacing.xs,
    fontWeight: '500',
    fontSize: '14px',
    color: colors.gray[700]
  },

  errorText: {
    color: colors.danger,
    fontSize: '12px',
    marginTop: spacing.xs
  },

  successText: {
    color: colors.success,
    fontSize: '14px',
    marginTop: spacing.xs
  },

  title: {
    fontSize: '32px',
    fontWeight: '800',
    color: colors.gray[900],
    marginBottom: spacing.lg,
    textAlign: 'center',
    letterSpacing: '-0.02em'
  },

  subtitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: colors.gray[800],
    marginBottom: spacing.md
  },

  text: {
    fontSize: '14px',
    color: colors.gray[600],
    lineHeight: '1.5'
  },

  badge: {
    base: {
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: borderRadius.full,
      fontSize: '12px',
      fontWeight: '500',
      letterSpacing: '0.5px'
    },
    success: {
      backgroundColor: colors.success,
      color: colors.white
    },
    warning: {
      backgroundColor: colors.warning,
      color: colors.white
    },
    danger: {
      backgroundColor: colors.danger,
      color: colors.white
    },
    gray: {
      backgroundColor: colors.gray[200],
      color: colors.gray[700]
    }
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: colors.white,
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
    boxShadow: shadows.sm,
    '& th': {
      backgroundColor: colors.gray[50],
      padding: spacing.md,
      textAlign: 'left',
      fontWeight: '600',
      fontSize: '12px',
      textTransform: 'uppercase',
      color: colors.gray[600],
      borderBottom: `1px solid ${colors.gray[200]}`
    },
    '& td': {
      padding: spacing.md,
      borderBottom: `1px solid ${colors.gray[100]}`,
      fontSize: '14px',
      color: colors.gray[700]
    },
    '& tbody tr:hover': {
      backgroundColor: colors.gray[50]
    }
  },

  mobileTable: {
    '& th, & td': {
      padding: spacing.sm,
      fontSize: '12px'
    }
  },

  loading: {
    textAlign: 'center',
    padding: spacing.xxl,
    color: colors.gray[500]
  },

  emptyState: {
    textAlign: 'center',
    padding: spacing.xxl,
    color: colors.gray[500],
    fontSize: '14px'
  }
};

// Media queries
export const mediaQueries = {
  mobile: '@media (max-width: 768px)',
  tablet: '@media (max-width: 1024px)',
  desktop: '@media (min-width: 1025px)'
};

// Utility functions
export const createButtonStyle = (variant = 'primary', size = 'base') => {
  return {
    ...commonStyles.button.base,
    ...commonStyles.button[variant],
    ...(size !== 'base' ? commonStyles.button[size] : {})
  };
};

export const createInputStyle = (hasError = false) => {
  return {
    ...commonStyles.input.base,
    ...(hasError ? commonStyles.input.error : {})
  };
};

export const createBadgeStyle = (variant = 'gray') => {
  return {
    ...commonStyles.badge.base,
    ...commonStyles.badge[variant]
  };
};